/* rbac/guards — declarative route/section guards. Clean ESM.
   <RequireRole action="platform.view"> wraps a route element; if the current
   effective role lacks the capability, an Access-Denied panel is shown (with a
   way back) instead of the protected content. Also exports <Gate> for inline
   conditional rendering of action buttons. */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useRbac } from './RbacContext';
import { CAPABILITIES, ROLE_META } from './roles';

export const AccessDenied = ({ action, fallbackTo = '/' }) => {
  const navigate = useNavigate();
  const { role } = useRbac();
  const required = CAPABILITIES[action];
  return (
    <div className="nx-rbac-denied">
      <span className="nx-rbac-denied-icon"><NexusIcon name="Shield" size={26} /></span>
      <h2 className="nx-rbac-denied-title">Access Restricted</h2>
      <p className="nx-rbac-denied-desc">
        This area requires the <strong>{required}</strong> role or higher.
        You are currently signed in as <Pill tone={ROLE_META[role]?.tone || 'gray'}>{role}</Pill>.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="primary" icon="ArrowRight" onClick={() => navigate(fallbackTo)}>Go to my workspace</Btn>
      </div>
    </div>
  );
};

/* Route-level guard */
export const RequireRole = ({ action, fallbackTo = '/', children }) => {
  const { can } = useRbac();
  if (!can(action)) return <AccessDenied action={action} fallbackTo={fallbackTo} />;
  return children;
};

/* Inline capability gate — renders children only if allowed (for buttons/sections). */
export const Gate = ({ action, children, fallback = null }) => {
  const { can } = useRbac();
  return can(action) ? children : fallback;
};
